import React, { useEffect, useState } from "react";
import { Handshake, ExternalLink } from "lucide-react";
import api from "../lib/api";

/**
 * SponsorCrawl — horizontal auto-scrolling strip of Tap IN TV's active
 * sponsors and currently-booked advertisers. Rotates across every station
 * page and the TV home. Pulls from GET /api/sponsorship/crawl which unions
 * `radio_ad_bookings` (status ∈ approved/live) with `sponsorship_inquiries`
 * (status=won).
 *
 * Gracefully renders nothing when the list is empty — no empty-state
 * placeholder since a sponsor-free strip just clutters the UI.
 */
export default function SponsorCrawl({ filterStationSlug = null, className = "" }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.get("/sponsorship/crawl")
      .then((r) => { if (mounted) setItems(r.data?.items || []); })
      .catch(() => { if (mounted) setItems([]); });
    return () => { mounted = false; };
  }, []);

  const list = filterStationSlug
    ? items.filter((i) => !i.station_slug || i.station_slug === filterStationSlug)
    : items;
  if (list.length === 0) return null;

  // Duplicate items so the marquee can loop seamlessly.
  const loop = [...list, ...list];

  return (
    <div
      data-testid="sponsor-crawl"
      className={`group relative overflow-hidden bg-gradient-to-r from-amber-500/10 via-fuchsia-500/10 to-tv-accent/10 border-y border-white/10 ${className}`}
    >
      <div className="absolute left-0 top-0 bottom-0 flex items-center gap-1.5 px-3 bg-black/80 z-10 border-r border-white/10">
        <Handshake className="w-3.5 h-3.5 text-tv-accent" />
        <span className="text-[10px] uppercase tracking-widest font-black text-tv-accent">Our sponsors</span>
      </div>
      <div className="flex items-center gap-8 py-2 pl-40 animate-[marquee_40s_linear_infinite] group-hover:[animation-play-state:paused]">
        {loop.map((s, i) => (
          <a
            key={`${s.id}-${i}`}
            data-testid={`sponsor-crawl-item-${s.id}`}
            href={s.website || "#"}
            target={s.website ? "_blank" : "_self"}
            rel="noreferrer"
            onClick={(e) => { if (!s.website) e.preventDefault(); }}
            className="shrink-0 inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-bold whitespace-nowrap"
          >
            {s.name}
            {s.website && <ExternalLink className="w-3 h-3 text-white/40" />}
          </a>
        ))}
      </div>
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
